import { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const PLATFORMS = ['Swiggy Instamart', 'Zepto', 'Blinkit', 'BigBasket'];

const AVAIL_COLOR = pct =>
    pct >= 80 ? { bg:'#D1FAE5', color:'#065F46' } :
    pct >= 50 ? { bg:'#FEF3C7', color:'#92400E' } :
                { bg:'#FEE2E2', color:'#991B1B' };

function exportXLSX(headers, rows, filename) {
    const XLSX = window.XLSX;
    if (!XLSX) { alert('SheetJS not loaded'); return; }
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Data');
    ws['!cols'] = headers.map(() => ({ wch: 22 }));
    XLSX.writeFile(wb, filename);
}

export default function PlatformAvailability({ user }) {
    const [platform,     setPlatform]     = useState('Swiggy Instamart');
    const [dates,        setDates]        = useState([]);
    const [selDate,      setSelDate]      = useState('');
    const [summary,      setSummary]      = useState(null);
    const [skus,         setSkus]         = useState([]);
    const [cities,       setCities]       = useState([]);
    const [activeTab,    setActiveTab]    = useState('skus');
    const [loading,      setLoading]      = useState(false);
    const [uploading,    setUploading]    = useState(false);
    const [uploadMsg,    setUploadMsg]    = useState(null);
    const [uploadPct,    setUploadPct]    = useState(0);
    const [skuSearch,    setSkuSearch]    = useState('');
    const [citySearch,   setCitySearch]   = useState('');
    const [expandedSku,  setExpandedSku]  = useState(null);
    const [pincodeData,  setPincodeData]  = useState([]);

    useEffect(() => { loadDates(); }, [platform]);
    useEffect(() => { if (selDate) { loadSummary(); loadSkus(); loadCities(); } }, [selDate, platform]);

    async function loadDates() {
        setSelDate(''); setDates([]); setSummary(null); setSkus([]); setCities([]);
        try {
            const res  = await fetch(`${API_BASE}/platform-availability?action=dates&platform=${enc(platform)}`);
            const data = await res.json();
            if (Array.isArray(data) && data.length > 0) {
                setDates(data);
                setSelDate(data[0]);
            }
        } catch(e) { console.error(e); }
    }

    async function loadSummary() {
        try {
            const res  = await fetch(`${API_BASE}/platform-availability?action=summary&platform=${enc(platform)}&date=${selDate}`);
            setSummary(await res.json());
        } catch(e) { console.error(e); }
    }

    async function loadSkus(search = '') {
        setLoading(true);
        try {
            const res  = await fetch(`${API_BASE}/platform-availability?action=skus&platform=${enc(platform)}&date=${selDate}&search=${enc(search)}`);
            const data = await res.json();
            setSkus(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
        finally { setLoading(false); }
    }

    async function loadCities() {
        try {
            const res  = await fetch(`${API_BASE}/platform-availability?action=cities&platform=${enc(platform)}&date=${selDate}`);
            const data = await res.json();
            setCities(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function loadPincodes(product) {
        if (expandedSku === product) { setExpandedSku(null); return; }
        setExpandedSku(product);
        try {
            const res  = await fetch(`${API_BASE}/platform-availability?action=pincode_detail&platform=${enc(platform)}&date=${selDate}&product=${enc(product)}`);
            const data = await res.json();
            setPincodeData(Array.isArray(data) ? data : []);
        } catch(e) { console.error(e); }
    }

    async function handleUpload(e) {
        const file = e.target.files[0]; if (!file) return;
        setUploading(true); setUploadMsg(null); setUploadPct(0);
        try {
            const XLSX = window.XLSX;
            if (!XLSX) { setUploadMsg({ type:'error', text:'SheetJS not loaded' }); return; }

            setUploadMsg({ type:'info', text:'Reading file...' });
            const buf  = await file.arrayBuffer();
            const wb   = XLSX.read(buf, { type:'array' });
            const ws   = wb.Sheets[wb.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(ws, { raw:false, defval:'' });

            if (json.length === 0) { setUploadMsg({ type:'error', text:'No data found in file' }); return; }

            // Map columns
            const allRows = json.map(r => ({
                product_name: String(r['Product Name with Variation'] || r['Product Name'] || r['product_name'] || '').trim(),
                city:         String(r['City'] || r['city'] || '').trim(),
                pincode:      String(r['Pincode'] || r['pincode'] || '').trim(),
                is_available: String(r['Available'] || '').toLowerCase() === 'true' || r['Available'] === true || r['Available'] === '1',
            })).filter(r => r.product_name && r.city && r.pincode);

            if (allRows.length === 0) {
                setUploadMsg({ type:'error', text:'No valid rows — columns needed: Product Name with Variation, City, Pincode, Available' });
                return;
            }

            // Deduplicate
            const seen = new Set();
            const rows = allRows.filter(r => {
                const key = `${r.product_name}|${r.city}|${r.pincode}`;
                if (seen.has(key)) return false;
                seen.add(key); return true;
            });

            // Detect upload date
            const rawDate    = json[0]?.['Date Formatted'] || json[0]?.['Date'] || new Date().toISOString().slice(0,10);
            const uploadDate = /^\d{4}-\d{2}-\d{2}$/.test(String(rawDate).trim()) ? String(rawDate).trim() : new Date().toISOString().slice(0,10);

            // Send in chunks of 5000
            const CHUNK_SIZE  = 5000;
            const totalChunks = Math.ceil(rows.length / CHUNK_SIZE);
            let totalInserted = 0;

            for (let c = 0; c < totalChunks; c++) {
                const chunk = rows.slice(c * CHUNK_SIZE, (c + 1) * CHUNK_SIZE);
                const pct   = Math.round(((c + 1) / totalChunks) * 100);
                setUploadPct(pct);
                setUploadMsg({ type:'info', text:`Uploading ${c+1}/${totalChunks} chunks (${pct}%)...` });

                const res = await fetch(`${API_BASE}/platform-availability?action=upload`, {
                    method: 'POST',
                    headers: { 'Content-Type':'application/json' },
                    body: JSON.stringify({
                        rows:         chunk,
                        platform,
                        upload_date:  uploadDate,
                        uploaded_by:  user?.username || 'Portal',
                        chunk:        c,
                        total_chunks: totalChunks
                    })
                });

                if (!res.ok) {
                    const err = await res.text();
                    setUploadMsg({ type:'error', text:`Chunk ${c+1} failed: ${err}` });
                    return;
                }

                const data     = await res.json();
                totalInserted += data.inserted || 0;
            }

            setUploadPct(100);
            setUploadMsg({ type:'success', text:`✓ Uploaded ${totalInserted.toLocaleString('en-IN')} rows for ${uploadDate}` });
            await loadDates();
            setTimeout(() => setSelDate(uploadDate), 500);

        } catch(err) {
            setUploadMsg({ type:'error', text:'Error: ' + err.message });
        } finally {
            setUploading(false);
        }
        e.target.value = '';
    }

    function enc(s) { return encodeURIComponent(s || ''); }

    const filteredSkus   = skus.filter(s => !skuSearch   || s.product_name.toLowerCase().includes(skuSearch.toLowerCase()));
    const filteredCities = cities.filter(c => !citySearch || c.city.toLowerCase().includes(citySearch.toLowerCase()));

    return (
        <div data-tutorial="platform-filters">
            {/* Page header */}
            <div style={{ marginBottom:'24px', display:'flex', justifyContent:'space-between', alignItems:'flex-end', flexWrap:'wrap', gap:'12px' }}>
                <div>
                    <h1 style={{ fontSize:'30px', fontWeight:800, color:'#000000', margin:0 }}>Platform Availability</h1>
                    <p style={{ fontSize:'14px', color:'#6B7280', marginTop:'4px', marginBottom:0 }}>
                        SKU availability by city and pincode across quick commerce platforms
                    </p>
                </div>
                <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
                    {uploadMsg && (
                        <div style={{ padding:'8px 16px', borderRadius:'8px', fontSize:'13px', fontWeight:600,
                            background: uploadMsg.type==='success'?'#D1FAE5': uploadMsg.type==='error'?'#FEE2E2':'#EFF6FF',
                            color:      uploadMsg.type==='success'?'#065F46': uploadMsg.type==='error'?'#991B1B':'#1D4ED8'
                        }}>
                            {uploadMsg.text}
                        </div>
                    )}
                    <label style={{ padding:'9px 18px', background: uploading?'#64748B':'#0F1F2E', color:'#fff', borderRadius:'8px', fontSize:'13px', fontWeight:700, cursor: uploading?'not-allowed':'pointer', opacity: uploading?0.7:1 }}>
                        {uploading ? `Uploading ${uploadPct}%...` : '⬆ Upload CSV / Excel'}
                        <input type="file" accept=".csv,.xlsx,.xls" style={{ display:'none' }} onChange={handleUpload} disabled={uploading} />
                    </label>
                </div>
            </div>

            {/* Progress bar */}
            {uploading && (
                <div style={{ marginBottom:'16px', background:'#F1F5F9', borderRadius:'8px', height:'6px', overflow:'hidden' }}>
                    <div style={{ width:`${uploadPct}%`, height:'100%', background:'#0EA5E9', borderRadius:'8px', transition:'width 0.3s' }} />
                </div>
            )}

            {/* Platform + Date controls */}
            <div style={{ display:'flex', gap:'12px', alignItems:'center', marginBottom:'20px', flexWrap:'wrap' }}>
                <div style={{ display:'flex', background:'#F1F5F9', borderRadius:'8px', padding:'3px', border:'1px solid #E2E8F0' }}>
                    {PLATFORMS.map(p => (
                        <button key={p} onClick={() => setPlatform(p)} style={{ padding:'7px 16px', borderRadius:'6px', border:'none', cursor:'pointer', background:platform===p?'#0F1F2E':'transparent', color:platform===p?'#fff':'#64748B', fontSize:'13px', fontWeight:platform===p?700:500, transition:'all 0.15s', whiteSpace:'nowrap' }}>
                            {p}
                        </button>
                    ))}
                </div>
                {dates.length > 0 && (
                    <select value={selDate} onChange={e => setSelDate(e.target.value)}
                        style={{ padding:'8px 14px', border:'1.5px solid #E2E8F0', borderRadius:'8px', fontSize:'14px', color:'#1A1A1A', outline:'none', background:'#fff', cursor:'pointer' }}>
                        {dates.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                )}
                {dates.length === 0 && !uploading && (
                    <div style={{ fontSize:'13px', color:'#94A3B8' }}>No data yet — upload a CSV or Excel file</div>
                )}
            </div>

            {selDate && summary && (
                <>
                    {/* KPI Cards */}
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:'14px', marginBottom:'20px' }}>
                        {[
                            ['Total SKUs',           summary.total_skus,            '#0F1F2E'],
                            ['Total Cities',         summary.total_cities,           '#1D4ED8'],
                            ['Total Pincodes',       summary.total_pincodes,         '#0F1F2E'],
                            ['Available Pincodes',   summary.available_pincodes,     '#065F46'],
                            ['Unavailable Pincodes', summary.unavailable_pincodes,   '#991B1B'],
                        ].map(([label, value, color]) => (
                            <div key={label} style={{ background:'#fff', borderRadius:'12px', padding:'18px 20px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                                <div style={{ fontSize:'11px', fontWeight:700, color:'#94A3B8', textTransform:'uppercase', letterSpacing:'0.8px', marginBottom:'6px' }}>{label}</div>
                                <div style={{ fontSize:'24px', fontWeight:800, color }}>{Number(value||0).toLocaleString('en-IN')}</div>
                            </div>
                        ))}
                    </div>

                    {/* Tabs */}
                    <div style={{ display:'inline-flex', background:'#F1F5F9', borderRadius:'8px', padding:'3px', border:'1px solid #E2E8F0', marginBottom:'20px' }}>
                        {[['skus','By SKU'],['cities','By City']].map(([k,l]) => (
                            <button key={k} onClick={() => setActiveTab(k)} style={{ padding:'8px 24px', borderRadius:'6px', border:'none', cursor:'pointer', background:activeTab===k?'#0F1F2E':'transparent', color:activeTab===k?'#fff':'#64748B', fontSize:'14px', fontWeight:activeTab===k?700:500, transition:'all 0.15s' }}>
                                {l}
                            </button>
                        ))}
                    </div>

                    {/* BY SKU */}
                    {activeTab === 'skus' && (
                        <div>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px', flexWrap:'wrap', gap:'10px' }}>
                                <input type="text" placeholder="Search product name..." value={skuSearch}
                                    onChange={e => { setSkuSearch(e.target.value); loadSkus(e.target.value); }}
                                    style={{ padding:'9px 14px', border:'1.5px solid #E2E8F0', borderRadius:'8px', fontSize:'14px', outline:'none', width:'320px' }} />
                                <button onClick={() => exportXLSX(
                                    ['Product Name','Total Cities','Avail Cities','Unavail Cities','Total Pincodes','Avail Pincodes','Unavail Pincodes','Avail %'],
                                    filteredSkus.map(s=>[s.product_name,s.total_cities,s.avail_cities,s.unavail_cities,s.total_pincodes,s.avail_pincodes,s.unavail_pincodes,s.avail_pct]),
                                    `Availability_SKUs_${selDate}.xlsx`
                                )} style={dlBtn}>⬇ Download</button>
                            </div>

                            {loading ? (
                                <div style={{ padding:'40px', textAlign:'center', color:'#94A3B8' }}>Loading...</div>
                            ) : (
                                <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                                    <div style={{ overflowX:'auto' }}>
                                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                                            <thead>
                                                <tr>
                                                    {['#','Product Name','Avail Cities','Unavail Cities','Avail Pincodes','Unavail Pincodes','Availability %',''].map(h => (
                                                        <th key={h} style={{ background:'#0F1F2E', color:'#fff', padding:'11px 14px', textAlign:'left', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredSkus.length === 0 ? (
                                                    <tr><td colSpan={8} style={{ padding:'40px', textAlign:'center', color:'#94A3B8' }}>No SKUs found</td></tr>
                                                ) : filteredSkus.map((s, i) => {
                                                    const ac         = AVAIL_COLOR(s.avail_pct);
                                                    const isExpanded = expandedSku === s.product_name;
                                                    return (
                                                        <>
                                                            <tr key={s.product_name} style={{ background:i%2===0?'#fff':'#F8FAFC', cursor:'pointer' }}
                                                                onClick={() => loadPincodes(s.product_name)}>
                                                                <td style={td}>{i+1}</td>
                                                                <td style={{ ...td, maxWidth:'320px', whiteSpace:'normal', fontWeight:500 }}>{s.product_name}</td>
                                                                <td style={{ ...td, color:'#065F46', fontWeight:600 }}>{s.avail_cities}</td>
                                                                <td style={{ ...td, color:'#991B1B' }}>{s.unavail_cities}</td>
                                                                <td style={{ ...td, color:'#065F46', fontWeight:600 }}>{s.avail_pincodes}</td>
                                                                <td style={{ ...td, color:'#991B1B' }}>{s.unavail_pincodes}</td>
                                                                <td style={td}>
                                                                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                                        <div style={{ flex:1, background:'#F1F5F9', borderRadius:'4px', height:'6px', minWidth:'60px' }}>
                                                                            <div style={{ width:`${s.avail_pct}%`, height:'100%', background:s.avail_pct>=80?'#10B981':s.avail_pct>=50?'#F59E0B':'#EF4444', borderRadius:'4px' }} />
                                                                        </div>
                                                                        <span style={{ fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'5px', background:ac.bg, color:ac.color, whiteSpace:'nowrap' }}>
                                                                            {s.avail_pct}%
                                                                        </span>
                                                                    </div>
                                                                </td>
                                                                <td style={td}>
                                                                    <span style={{ fontSize:'12px', color:'#94A3B8' }}>{isExpanded ? '▲' : '▼'}</span>
                                                                </td>
                                                            </tr>
                                                            {isExpanded && (
                                                                <tr key={`${s.product_name}_exp`}>
                                                                    <td colSpan={8} style={{ padding:0, background:'#F8FAFC', borderBottom:'2px solid #E2E8F0' }}>
                                                                        <div style={{ padding:'16px 24px' }}>
                                                                            <div style={{ fontSize:'13px', fontWeight:700, color:'#1A1A1A', marginBottom:'12px' }}>
                                                                                Pincode Detail — {s.product_name}
                                                                            </div>
                                                                            <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', maxHeight:'200px', overflowY:'auto' }}>
                                                                                {pincodeData.map((p, j) => (
                                                                                    <div key={j} style={{ padding:'4px 10px', borderRadius:'6px', fontSize:'12px', fontWeight:600, background:p.is_available?'#D1FAE5':'#FEE2E2', color:p.is_available?'#065F46':'#991B1B', whiteSpace:'nowrap' }}>
                                                                                        {p.pincode} <span style={{ fontWeight:400, fontSize:'11px' }}>({p.city})</span>
                                                                                    </div>
                                                                                ))}
                                                                            </div>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* BY CITY */}
                    {activeTab === 'cities' && (
                        <div>
                            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'14px', flexWrap:'wrap', gap:'10px' }}>
                                <input type="text" placeholder="Search city..." value={citySearch}
                                    onChange={e => setCitySearch(e.target.value)}
                                    style={{ padding:'9px 14px', border:'1.5px solid #E2E8F0', borderRadius:'8px', fontSize:'14px', outline:'none', width:'280px' }} />
                                <button onClick={() => exportXLSX(
                                    ['City','Total SKUs','Avail SKUs','Total Pincodes','Avail Pincodes','Unavail Pincodes','Avail %'],
                                    filteredCities.map(c=>[c.city,c.total_skus,c.avail_skus,c.total_pincodes,c.avail_pincodes,c.unavail_pincodes,c.avail_pct]),
                                    `Availability_Cities_${selDate}.xlsx`
                                )} style={dlBtn}>⬇ Download</button>
                            </div>

                            <div style={{ background:'#fff', borderRadius:'12px', boxShadow:'0 2px 8px rgba(0,0,0,0.06)', overflow:'hidden' }}>
                                <div style={{ overflowX:'auto' }}>
                                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'13px' }}>
                                        <thead>
                                            <tr>
                                                {['#','City','Total SKUs','Avail SKUs','Total Pincodes','Avail Pincodes','Unavail Pincodes','Availability %'].map(h => (
                                                    <th key={h} style={{ background:'#0F1F2E', color:'#fff', padding:'11px 14px', textAlign:'left', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap' }}>{h}</th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredCities.length === 0 ? (
                                                <tr><td colSpan={8} style={{ padding:'40px', textAlign:'center', color:'#94A3B8' }}>No cities found</td></tr>
                                            ) : filteredCities.map((c, i) => {
                                                const ac = AVAIL_COLOR(c.avail_pct);
                                                return (
                                                    <tr key={c.city} style={{ background:i%2===0?'#fff':'#F8FAFC' }}>
                                                        <td style={td}>{i+1}</td>
                                                        <td style={{ ...td, fontWeight:600 }}>{c.city}</td>
                                                        <td style={td}>{c.total_skus}</td>
                                                        <td style={{ ...td, color:'#065F46', fontWeight:600 }}>{c.avail_skus}</td>
                                                        <td style={td}>{c.total_pincodes}</td>
                                                        <td style={{ ...td, color:'#065F46', fontWeight:600 }}>{c.avail_pincodes}</td>
                                                        <td style={{ ...td, color:'#991B1B' }}>{c.unavail_pincodes}</td>
                                                        <td style={td}>
                                                            <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                                                                <div style={{ flex:1, background:'#F1F5F9', borderRadius:'4px', height:'6px', minWidth:'60px' }}>
                                                                    <div style={{ width:`${c.avail_pct}%`, height:'100%', background:c.avail_pct>=80?'#10B981':c.avail_pct>=50?'#F59E0B':'#EF4444', borderRadius:'4px' }} />
                                                                </div>
                                                                <span style={{ fontSize:'11px', fontWeight:700, padding:'2px 8px', borderRadius:'5px', background:ac.bg, color:ac.color, whiteSpace:'nowrap' }}>
                                                                    {c.avail_pct}%
                                                                </span>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Empty state */}
            {!selDate && !uploading && (
                <div style={{ background:'#fff', borderRadius:'14px', padding:'60px', textAlign:'center', boxShadow:'0 2px 8px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize:'40px', marginBottom:'16px' }}>📦</div>
                    <div style={{ fontSize:'18px', fontWeight:700, color:'#1A1A1A', marginBottom:'8px' }}>No availability data yet</div>
                    <div style={{ fontSize:'14px', color:'#6B7280', marginBottom:'20px' }}>Upload a CSV or Excel file with the following columns:</div>
                    <div style={{ display:'inline-flex', gap:'8px', flexWrap:'wrap', justifyContent:'center' }}>
                        {['Product Name with Variation','City','Pincode','Available'].map(col => (
                            <span key={col} style={{ padding:'4px 12px', background:'#F1F5F9', borderRadius:'6px', fontSize:'12px', fontWeight:600, color:'#475569' }}>{col}</span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

const td    = { padding:'11px 14px', borderBottom:'1px solid #F1F5F9', color:'#334155', verticalAlign:'middle', whiteSpace:'nowrap' };
const dlBtn = { padding:'7px 16px', background:'#000', color:'#fff', border:'none', borderRadius:'7px', fontSize:'13px', fontWeight:600, cursor:'pointer' };